import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ArrowRight, Zap, Trophy, Sparkles, Lock, Globe, Cpu,
  Layers, ShieldCheck, Check, Mic,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Logo, LogoMark } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/contexts/AuthContext";
import { getPlans } from "@/lib/api";
import { ChatSupportWidget } from "@/components/ChatSupportWidget";
import { Seo } from "@/components/Seo";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    getPlans().then((r) => setPlans(r.data.plans)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Seo
        title="Sabzy AI — ChatGPT, Claude & Gemini in one place, with Web Search & Racing"
        description="Sabzy AI gives you 19 frontier models — ChatGPT Astra, GPT-5.6, Claude Opus 5, Gemini 3.1 Pro and more — in one chat. Race models in parallel, search the live web with citations, analyse images & PDFs. 7-day Pro Max trial, no card. Wholesale API for developers."
        path="/"
      />
      {/* ===== NAV ===== */}
      <header className="sticky top-0 z-50 border-b border-border/40 bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3.5 lg:px-10">
          <Logo />
          <nav className="hidden items-center gap-7 md:flex">
            <a href="#capabilities" className="text-sm text-muted-foreground transition hover:text-foreground" data-testid="nav-features-link">Capabilities</a>
            <a href="#racing" className="text-sm text-muted-foreground transition hover:text-foreground" data-testid="nav-racing-link">Racing</a>
            <Link to="/models" className="text-sm text-muted-foreground transition hover:text-foreground" data-testid="nav-models-link">Models & API</Link>
            <a href="#plans" className="text-sm text-muted-foreground transition hover:text-foreground" data-testid="nav-plans-link">Plans</a>
            <Link to="/help" className="text-sm text-muted-foreground transition hover:text-foreground" data-testid="nav-help-link">Help</Link>
            <Link to="/faq" className="text-sm text-muted-foreground transition hover:text-foreground" data-testid="nav-faq-link">FAQ</Link>
            <Link to="/contact" className="text-sm text-muted-foreground transition hover:text-foreground" data-testid="nav-contact-link">Contact</Link>
          </nav>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            {user ? (
              <Button onClick={() => navigate("/chat")} className="h-9 rounded-full px-4 text-sm font-medium" data-testid="open-chat-btn">
                Open chat <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            ) : (
              <Button onClick={() => navigate("/chat")} className="h-9 rounded-full px-4 text-sm font-medium" data-testid="nav-signup-btn">
                Try Sabzy
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden">
        {/* Background atmosphere */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute left-1/2 top-0 h-[640px] w-[1100px] -translate-x-1/2 -translate-y-1/4 rounded-full bg-primary/12 blur-[140px]" />
          <div className="absolute left-1/2 top-[-10%] h-[300px] w-[300px] -translate-x-1/2 rounded-full bg-primary/20 blur-[80px]" />
        </div>

        <div className="mx-auto max-w-7xl px-6 pt-24 pb-20 text-center lg:px-10 lg:pt-32 lg:pb-28">
          {/* Top pill */}
          <div className="mb-8 inline-flex animate-fade-up items-center gap-2 rounded-full border border-border/60 bg-card/80 px-3.5 py-1.5 text-xs backdrop-blur-sm" data-testid="hero-badge">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
            </span>
            <span className="text-foreground/80">New · ChatGPT Astra, Claude Opus 5 & Gemini 3.1 Pro</span>
            <span className="text-muted-foreground">·</span>
            <ArrowRight className="h-3 w-3 text-primary" />
          </div>

          {/* Headline — brand poetry. Keyword keywords lean on sub + sr-only block below. */}
          <h1
            className="mx-auto max-w-4xl animate-fade-up text-balance text-5xl font-medium leading-[1.04] tracking-[-0.04em] sm:text-6xl lg:text-7xl xl:text-[80px]"
            style={{ fontFamily: "'Outfit', sans-serif", animationDelay: "80ms" }}
          >
            The most powerful AI,<br />
            <span className="copper-text">made effortless.</span>
          </h1>

          {/* Visually-hidden but indexed secondary headline for SEO */}
          <p className="sr-only">
            Sabzy AI — one chat for OpenAI ChatGPT (GPT-6 Astra, GPT-5.6, GPT-5.4), Anthropic Claude (Opus 5, Opus 4.8, Sonnet 5)
            and Google Gemini (3.1 Pro, 3.8 Flash), with live web search, racing mode, multimodal vision for images and PDFs,
            a 7-day Pro Max trial with no credit card, and a wholesale developer API at 50% off retail.
          </p>

          {/* Sub */}
          <p
            className="mx-auto mt-7 max-w-2xl animate-fade-up text-base leading-relaxed text-muted-foreground sm:text-lg"
            style={{ animationDelay: "160ms" }}
          >
            ChatGPT, Claude and Gemini — 19 frontier models behind one composer. Switch models mid-chat,
            race them in parallel, search the live web with citations. Free for 7 days, no card.
          </p>

          {/* CTAs */}
          <div
            className="mt-10 flex animate-fade-up flex-col items-center justify-center gap-3 sm:flex-row"
            style={{ animationDelay: "240ms" }}
          >
            <Button
              size="lg"
              onClick={() => navigate("/chat")}
              className="group h-12 rounded-full px-7 text-sm font-medium shadow-lg shadow-primary/20"
              data-testid="hero-try-now-btn"
            >
              Try Sabzy for free
              <ArrowRight className="ml-2 h-4 w-4 transition group-hover:translate-x-0.5" />
            </Button>
            <Button
              size="lg"
              variant="ghost"
              onClick={() => {
                document.getElementById("capabilities")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="h-12 rounded-full px-6 text-sm font-medium text-muted-foreground hover:text-foreground"
              data-testid="hero-cta-btn"
            >
              See capabilities <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>

          {/* Quick trust row */}
          <div
            className="mt-12 flex animate-fade-up flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs text-muted-foreground"
            style={{ animationDelay: "320ms" }}
          >
            <div className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-primary" /> No credit card</div>
            <div className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-primary" /> Code-gated tiers</div>
            <div className="flex items-center gap-1.5"><Check className="h-3.5 w-3.5 text-primary" /> Encrypted by default</div>
          </div>

          {/* Hero visual: terminal preview */}
          <div
            className="mx-auto mt-20 max-w-5xl animate-fade-up"
            style={{ animationDelay: "420ms" }}
          >
            <HeroPreview />
          </div>
        </div>
      </section>

      {/* ===== LOGOS / SOCIAL PROOF row ===== */}
      <section className="border-y border-border/40 bg-card/30">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-6 px-6 py-6 text-xs text-muted-foreground lg:px-10">
          <span className="font-medium text-foreground">Powered by</span>
          <div className="flex flex-wrap items-center gap-x-10 gap-y-3 opacity-80">
            <span className="font-semibold">OpenAI GPT-6 Astra</span>
            <span className="font-semibold">Anthropic Claude Opus 5</span>
            <span className="font-semibold">Google Gemini 3.1 Pro</span>
            <span className="font-semibold">Tavily Search</span>
            <span className="font-semibold">AgentMail OTP</span>
            <span className="font-semibold">Multimodal Vision</span>
          </div>
        </div>
      </section>

      {/* ===== CAPABILITIES ===== */}
      <section id="capabilities" className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-primary">Capabilities</div>
          <h2
            className="text-4xl font-medium tracking-[-0.03em] sm:text-5xl"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Everything you need.<br />
            <span className="text-muted-foreground">Nothing you don&apos;t.</span>
          </h2>
        </div>

        <div className="mx-auto mt-16 grid max-w-6xl grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[
            { icon: Cpu, title: "Every frontier model", desc: "ChatGPT Astra, Claude Opus 5, Gemini 3.1 Pro and 16 more — pick any model per message, no separate subscriptions." },
            { icon: Trophy, title: "Pro Max Racing", desc: "Fire GPT, Claude and Gemini in parallel. See whichever answers first — usually under a second." },
            { icon: Mic, title: "Voice cloning", desc: "Clone any voice from a 10–30s sample, then make it say anything in 6 languages. Powered by OpenVoice V2." },
            { icon: Globe, title: "Live web search", desc: "Tavily-powered RAG at query time. Get fresh answers with inline citations." },
            { icon: Layers, title: "Multimodal", desc: "Drop images, PDFs, documents, or code. Sabzy analyzes them natively." },
            { icon: Zap, title: "Wholesale API", desc: "Resell the same 19 models through one Sabzy key — OpenAI & Anthropic SDK compatible, 50% off retail." },
          ].map((f, i) => (
            <FeatureCard key={f.title} {...f} delay={i * 60} />
          ))}
        </div>
      </section>

      {/* ===== RACING SHOWCASE ===== */}
      <section id="racing" className="relative overflow-hidden border-y border-border/40 bg-card/30">
        <div className="absolute inset-0 -z-10">
          <div className="absolute right-0 top-0 h-[400px] w-[400px] rounded-full bg-primary/10 blur-[120px]" />
        </div>
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 py-24 lg:grid-cols-2 lg:px-10 lg:py-32">
          <div className="animate-fade-up">
            <div className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-primary">Pro Max exclusive</div>
            <h2
              className="text-4xl font-medium leading-[1.05] tracking-[-0.03em] sm:text-5xl lg:text-6xl"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Three models race.<br />
              <span className="copper-text">The fastest wins.</span>
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-muted-foreground">
              Every racing prompt fires GPT-5.4, Claude Sonnet 4.6 and Gemini 3.1 Pro simultaneously.
              You see the response from whichever answers first — three labs, one winner.
            </p>
            <div className="mt-8 space-y-2.5">
              {[
                { name: "GPT-5.4", desc: "OpenAI · balanced", t: "1.24s" },
                { name: "Claude Sonnet 4.6", desc: "Anthropic · precise", t: "0.81s" },
                { name: "Gemini 3.1 Pro", desc: "Google · lightning fast", t: "0.42s", winner: true },
              ].map((m, i) => (
                <div
                  key={m.name}
                  className={`flex items-center justify-between rounded-xl border px-4 py-3 transition-all ${
                    m.winner
                      ? "border-primary/40 bg-primary/5 shadow-[0_0_24px_-8px_hsl(var(--primary)/0.4)]"
                      : "border-border bg-background/40"
                  }`}
                  data-testid={`race-model-${i}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`grid h-8 w-8 place-items-center rounded-lg text-xs font-semibold ${
                      m.winner ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"
                    }`}>
                      {i + 1}
                    </div>
                    <div>
                      <div className="text-sm font-medium">{m.name}</div>
                      <div className="text-xs text-muted-foreground">{m.desc}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {m.winner && <Trophy className="h-3.5 w-3.5 text-primary" />}
                    <span className={`text-xs font-medium ${m.winner ? "text-primary" : "text-muted-foreground"}`}>
                      {m.t}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="animate-fade-up" style={{ animationDelay: "120ms" }} data-testid="racing-graphic">
            <RacingVisual />
          </div>
        </div>
      </section>

      {/* ===== PLANS ===== */}
      <section id="plans" className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <div className="mx-auto mb-16 max-w-3xl text-center">
          <div className="mb-3 text-xs font-medium uppercase tracking-[0.2em] text-primary">Plans</div>
          <h2
            className="text-4xl font-medium tracking-[-0.03em] sm:text-5xl"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Choose your power level.
          </h2>
          <p className="mt-4 text-muted-foreground">
            Every tier unlocks with a promo code. No card. No subscription.
          </p>
        </div>

        <div className="mx-auto grid max-w-6xl gap-5 md:grid-cols-3" data-testid="plans-grid">
          {plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              onSelect={() => navigate("/chat")}
            />
          ))}
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="relative overflow-hidden border-t border-border/40">
        <div className="absolute inset-0 -z-10">
          <div className="absolute left-1/2 top-1/2 h-[450px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/14 blur-[140px]" />
        </div>
        <div className="mx-auto max-w-3xl px-6 py-24 text-center lg:py-32">
          <div className="mx-auto mb-8 grid h-16 w-16 place-items-center">
            <LogoMark size={64} />
          </div>
          <h2
            className="text-4xl font-medium tracking-[-0.03em] sm:text-6xl"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Start chatting now.
          </h2>
          <p className="mx-auto mt-5 max-w-lg text-muted-foreground">
            Anonymous preview, free. Save your chats and unlock ChatGPT Astra, Claude Opus 5 and Gemini Pro with one click.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button
              size="lg"
              onClick={() => navigate("/chat")}
              className="h-12 rounded-full px-8 text-sm font-medium shadow-lg shadow-primary/25"
              data-testid="cta-signup-btn"
            >
              Open Sabzy <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="border-t border-border/40">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-6 py-8 sm:flex-row lg:px-10">
          <div className="flex items-center gap-3">
            <Logo size={22} />
            <span className="hidden text-xs text-muted-foreground sm:inline">
              © {new Date().getFullYear()} Sabzy AI · Premium intelligence
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
            <a href="#capabilities" className="hover:text-foreground">Capabilities</a>
            <a href="#racing" className="hover:text-foreground">Racing</a>
            <Link to="/models" className="hover:text-foreground">Models &amp; API</Link>
            <a href="#plans" className="hover:text-foreground">Plans</a>
            <Link to="/help" className="hover:text-foreground" data-testid="footer-help-link">Help</Link>
            <Link to="/faq" className="hover:text-foreground" data-testid="footer-faq-link">FAQ</Link>
            <Link to="/contact" className="hover:text-foreground" data-testid="footer-contact-link">Contact</Link>
            <Link to="/privacy" className="hover:text-foreground" data-testid="footer-privacy-link">Privacy</Link>
          </div>
        </div>
      </footer>
      <ChatSupportWidget />
    </div>
  );
}

/* ============ Components ============ */

function FeatureCard({ icon: Icon, title, desc, delay = 0 }) {
  return (
    <div
      className="group animate-fade-up rounded-2xl border border-border/60 bg-card/40 p-7 transition-all hover:border-border hover:bg-card/80 hover:shadow-lg hover:shadow-primary/5"
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="mb-5 grid h-10 w-10 place-items-center rounded-xl bg-primary/12 text-primary transition group-hover:bg-primary/20">
        <Icon className="h-4.5 w-4.5" strokeWidth={1.8} />
      </div>
      <h3
        className="text-lg font-semibold tracking-tight"
        style={{ fontFamily: "'Outfit', sans-serif" }}
      >
        {title}
      </h3>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{desc}</p>
    </div>
  );
}

function PlanCard({ plan, onSelect }) {
  const highlighted = plan.highlighted;
  return (
    <div
      className={`relative flex flex-col rounded-2xl border p-7 transition-all ${
        highlighted
          ? "border-primary/40 bg-card shadow-2xl shadow-primary/10"
          : "border-border/60 bg-card/40 hover:border-border"
      }`}
      data-testid={`plan-card-${plan.id}`}
    >
      {highlighted && (
        <div className="absolute -top-3 left-1/2 inline-flex -translate-x-1/2 items-center gap-1 rounded-full bg-primary px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-primary-foreground">
          <Sparkles className="h-2.5 w-2.5" />
          Recommended
        </div>
      )}
      <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{plan.tagline}</div>
      <div
        className="mt-1 text-2xl font-semibold tracking-tight"
        style={{ fontFamily: "'Outfit', sans-serif" }}
      >
        {plan.name}
      </div>
      <div className="mt-1 text-sm text-muted-foreground">{plan.model}</div>

      <div className="my-6 h-px bg-border/60" />

      <ul className="flex-1 space-y-3 text-sm">
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5">
            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <Button
        onClick={onSelect}
        variant={highlighted ? "default" : "outline"}
        className="mt-8 h-10 w-full rounded-xl text-sm font-medium"
        data-testid={`select-plan-${plan.id}-btn`}
      >
        Try Sabzy <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

/* === Hero preview (looks like a real chat thread) === */
function HeroPreview() {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card/70 shadow-2xl shadow-black/30 backdrop-blur-xl">
      {/* Window chrome */}
      <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
        <div className="flex items-center gap-2">
          <LogoMark size={22} />
          <span className="text-xs font-medium text-foreground">Sabzy</span>
          <span className="text-xs text-muted-foreground">· New chat</span>
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-primary">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
          </span>
          Live
        </div>
      </div>

      <div className="grid gap-6 p-7 text-left lg:grid-cols-[1fr_1.2fr] lg:p-10">
        {/* user msg */}
        <div className="space-y-4">
          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">You</div>
          <div className="rounded-2xl rounded-tl-md bg-secondary px-4 py-3 text-sm leading-relaxed">
            What are the major AI breakthroughs in early 2026? Cite sources.
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-1 text-[11px] text-primary">
              <Globe className="h-3 w-3" /> Web search
            </span>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-[11px] text-muted-foreground">
              <Cpu className="h-3 w-3" /> 4.7 Max
            </span>
          </div>
        </div>

        {/* assistant msg */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <LogoMark size={20} />
            <span className="font-semibold text-foreground">Sabzy</span>
            <span className="opacity-50">·</span>
            <span>ChatGPT Astra</span>
            <span className="opacity-50">·</span>
            <span>1.24s</span>
            <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-primary">
              <Globe className="h-2.5 w-2.5" /> Web
            </span>
          </div>
          <div className="space-y-2 text-sm leading-relaxed">
            <p>Q1 2026 saw three pivotal advances<span className="text-primary">[1]</span>:</p>
            <ul className="space-y-1.5">
              <li className="flex gap-2"><span className="text-primary">•</span> <span><strong>Long-horizon agents</strong> with 200+ step task chains<span className="text-primary">[2]</span></span></li>
              <li className="flex gap-2"><span className="text-primary">•</span> <span><strong>Multimodal reasoning</strong> at video frame rates<span className="text-primary">[3]</span></span></li>
              <li className="flex gap-2"><span className="text-primary">•</span> <span><strong>Enterprise patterns</strong> standardizing across hyperscalers<span className="text-primary">[4]</span></span></li>
            </ul>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-1.5 rounded-lg border border-border/60 bg-secondary/30 p-2">
            {["nature.com", "mit.edu", "arxiv.org", "openai.com"].map((s, i) => (
              <div key={s} className="flex items-center gap-2 rounded-md bg-background/60 px-2 py-1.5 text-[11px]">
                <span className="grid h-3.5 w-3.5 place-items-center rounded bg-primary/15 text-[9px] font-bold text-primary">{i + 1}</span>
                <span className="truncate text-muted-foreground">{s}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* === Racing visual === */
function RacingVisual() {
  return (
    <div className="relative">
      <div className="absolute -inset-6 rounded-3xl bg-primary/10 blur-3xl" />
      <div className="relative overflow-hidden rounded-2xl border border-border bg-card/70 p-8 backdrop-blur-xl shadow-xl">
        <div className="mb-6 flex items-center justify-between text-xs text-muted-foreground">
          <span className="font-medium">Pro Max · Racing</span>
          <span className="inline-flex items-center gap-1.5 text-primary">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
            In progress
          </span>
        </div>

        <div className="space-y-5">
          {[
            { name: "GPT-5.4", pct: 88, time: "1.24s" },
            { name: "Claude Sonnet 4.6", pct: 95, time: "0.81s" },
            { name: "Gemini 3.1 Pro", pct: 100, time: "0.42s", winner: true },
          ].map((m, i) => (
            <div key={m.name}>
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-sm font-medium">{m.name}</span>
                <span className={`text-xs ${m.winner ? "text-primary" : "text-muted-foreground"}`}>
                  {m.time}
                </span>
              </div>
              <div className="relative h-2 overflow-hidden rounded-full bg-secondary">
                <div
                  className={`absolute inset-y-0 left-0 rounded-full ${
                    m.winner ? "bg-primary" : "bg-foreground/30"
                  }`}
                  style={{
                    width: `${m.pct}%`,
                    transition: `width 1.${i}s cubic-bezier(0.16, 1, 0.3, 1)`,
                  }}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex items-center justify-between rounded-xl border border-primary/30 bg-primary/10 px-4 py-3">
          <div className="flex items-center gap-2.5">
            <Trophy className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Gemini 3.1 Pro won</span>
          </div>
          <span className="text-sm font-medium text-primary">0.42s</span>
        </div>
      </div>
    </div>
  );
}
